import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSearch } from "@/components/bodyBlocks/cardsBlock/buscarFiltrar/SearchContext";
import { Seccion, Profesion, PerfilProfesional } from "@/types";
import { ResponsiveImage } from "@/components/ui";
import SearchInput from "@/components/bodyBlocks/cardsBlock/buscarFiltrar/Buscador";
import { safeFetch } from "@/api/safeFetch";
import { buildImageUrl } from "@/api/buildImageUrl";  // <-- ✅ NUEVO IMPORT


const normalize = (text?: string) =>
  (text ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const notNull = <T,>(v: T | null): v is T => v !== null;

const parseQuery = (q: string) => {
  const raw = normalize(q);
  const cpRegex = /\b(?:cp|c\.p\.|codigo postal|código postal)\b/gi;
  const locRegex = /\b(?:en|de|por|del|la|el|los|las|con|para|provincia|localidad)\b/gi;
  let term = "";
  let location = "";

  if (cpRegex.test(raw)) {
    const parts = raw.split(cpRegex).map((p) => p.trim()).filter(Boolean);
    term = parts[0] || "";
    location = parts[1] || "";
  } else if (locRegex.test(raw)) {
    const parts = raw.split(locRegex).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      term = parts[0];
      location = parts.slice(1).join(" ");
    } else {
      term = parts[0] || raw;
    }
  } else {
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length > 1) {
      term = parts.slice(0, -1).join(" ");
      location = parts[parts.length - 1];
    } else {
      term = raw;
    }
  }

  return { term: term.trim(), location: location.trim() };
};

const matchesTerm = (text: string, term: string): boolean => {
  const t = normalize(text);
  const q = normalize(term);
  if (!q) return false;

  const pluralize = (w: string) => (w.endsWith("s") ? w : w + "s");
  const singularize = (w: string) => (w.endsWith("s") ? w.slice(0, -1) : w);

  const variants = [q, pluralize(q), singularize(q)];

  // 🔑 CLAVE: dividir en palabras
  const words = t.split(/\s+/);

  return words.some(word =>
    variants.some(v => word.startsWith(v))
  );
};

const filterData = (data: Seccion[], rawQuery: string): Seccion[] => {
  const q = rawQuery.trim();
  if (!q) return [];
  const { term, location } = parseQuery(q);
  const locationNormalized = normalize(location);

  return data
    .map((seccion) => {
      const seccionMatches = matchesTerm(seccion.nombreSeccion, term);
      const profesionesFiltered: Profesion[] = (seccion.profesiones ?? [])
        .map((prof) => {
          const profMatches = matchesTerm(prof.nombre_profesion, term);

          const perfilesFiltered: PerfilProfesional[] = (prof.perfiles ?? []).filter((perf) => {

            const text = [
              perf.nombre_comercial,
              perf.nombre_empresa,
              prof.nombre_profesion,
              perf.descripcion
            ]
              .filter(Boolean)
              .join(" ");

            const textMatches = matchesTerm(text, term);

            const locationMatches = !location
              ? true
              : [
                perf.direccion?.provincia,
                perf.direccion?.localidad,
                perf.direccion?.codigo_postal?.toString(),
              ]
                .map((v) => normalize(v ?? ""))
                .some((loc) => loc.includes(locationNormalized));

            return textMatches && locationMatches;
          });

          if (profMatches || perfilesFiltered.length > 0) {
            return { ...prof, perfiles: perfilesFiltered };
          }
          return null;
        })
        .filter(notNull);

      if (seccionMatches || profesionesFiltered.length > 0) {
        return { ...seccion, profesiones: profesionesFiltered };
      }
      return null;
    })
    .filter(notNull)
    .sort((a, b) => normalize(a.nombreSeccion).localeCompare(normalize(b.nombreSeccion)));
};

const Nav: React.FC<{ mobileMode?: boolean; onCloseMobile?: () => void }> = ({
  mobileMode = false,
  onCloseMobile,
}) => {
  const navigate = useNavigate();
  const { query, setQuery } = useSearch();
  const [data, setData] = useState<Seccion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const panelRef = useRef<HTMLDivElement | null>(null);


  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 220);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await safeFetch(`/api/jerarquia`,);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: Seccion[] = await res.json();
        setData(Array.isArray(json) ? json : []);
      } catch (err: any) {
        console.error("Error cargando jerarquía:", err);
        setError(err?.message ?? "Error desconocido");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handle = (ev: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(ev.target as Node)) {
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [setQuery]);

  const results = useMemo(
    () => filterData(data, debouncedQuery ?? ""),
    [data, debouncedQuery]
  );

  const panelOpen = (mobileMode && (debouncedQuery ?? "").trim().length > 0);

  return (
    <div className={`relative w-full ${mobileMode ? "px-4 py-2" : "max-w-xl mx-auto"}`}>
      {/* Input de búsqueda con animación */}
      <motion.div
        initial={{ y: -12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full rounded-lg"
          placeholder="Busca un servicio..."
          mobileMode={mobileMode}
          panelOpen={panelOpen}
        />
      </motion.div>

      <AnimatePresence>
        {(debouncedQuery ?? "").trim().length > 0 && (
          <motion.div
            ref={panelRef}
            className={`absolute top-full left-0 right-0 mt-1 z-50 rounded-xl ${mobileMode ? "px-4" : ""}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            {/* Contenedor principal del panel de resultados */}
            <div className="relative w-full bg-zinc-100 border border-zinc-400 shadow-md rounded-xl">

              {/* Header del panel */}
              <div className="flex items-center justify-between p-4 border-b rounded-t-xl border-zinc-300">
                <h3 className="text-lg font-semibold text-zinc-800">Resultados</h3>
                <button
                  className="px-3 py-1 text-sm text-zinc-600"
                  onClick={() => {
                    setQuery("");
                    if (mobileMode && onCloseMobile) onCloseMobile();
                  }}
                >
                  Cerrar
                </button>
              </div>

              {/* Contenedor scrollable con borde inferior redondeado */}
              <div
                className="p-4 max-h-[70vh] overflow-y-auto rounded-b-xl
                         scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-zinc-200"
              >
                {/* Comentario: Aquí aplicamos color al scroll usando clases Tailwind */}
                {!loading && !error && results.length > 0 && (
                  <div className="space-y-6 rounded-xl ">
                    {results.map((seccion) => (
                      <div
                        key={seccion.id ?? seccion.nombreSeccion}
                        className="bg-zinc-400 rounded-xl shadow-sm border p-4"
                      >
                        {/* Contenido de la sección */}
                        <div className="flex items-center gap-3 mb-4">
                          <ResponsiveImage
                            src={buildImageUrl(seccion.foto_perfil)}
                            alt={seccion.nombreSeccion}
                            className="w-14 h-14 rounded-lg border"
                            type="logo"
                          />
                          <h4 className="text-xl font-bold text-blue-700">{seccion.nombreSeccion}</h4>
                        </div>

                        {/* Profesiones */}
                        {seccion.profesiones?.sort((a, b) =>
                          normalize(a.nombre_profesion).localeCompare(normalize(b.nombre_profesion))
                        ).map((prof) => {
                          const profesionPath = `/profesionales/${encodeURIComponent(prof.nombre_profesion)}`;
                          return (
                            <div
                              key={prof.id ?? prof.nombre_profesion}
                              className="flex-shrink-0 bg-zinc-50 border rounded-lg p-4 shadow-sm mt-4 cursor-pointer flex flex-col items-center"
                              onClick={() => {
                                setQuery("");
                                if (mobileMode && onCloseMobile) onCloseMobile();
                                navigate(profesionPath);
                              }}
                            >
                              {/* Imagen y nombre de la profesión */}
                              <ResponsiveImage
                                src={buildImageUrl(prof.imagen_url)}
                                alt={prof.nombre_profesion}
                                className="w-24 h-24 rounded-lg mb-2 object-contain border"
                                type="logo"
                              />
                              <div className="text-center text-blue-800 font-semibold text-sm">{prof.nombre_profesion}</div>

                              {/* Profesionales */}
                              {prof.perfiles && prof.perfiles.length > 0 && (
                                <div className="mt-4 w-full">
                                  <h6 className="text-sm font-semibold text-zinc-600 mb-2">Profesionales:</h6>
                                  <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-blue-500 scrollbar-track-gray-200">
                                    {prof.perfiles.map((perf) => {
                                      const perfilPath = `/profesional/${encodeURIComponent(
                                        prof.nombre_profesion
                                      )}/${encodeURIComponent(String(perf.perfil_profesional_id))}`;
                                      return (
                                        <div
                                          key={perf.perfil_profesional_id}
                                          className="flex-shrink-0 w-44 rounded-lg bg-zinc-400 p-2 cursor-pointer border shadow snap-start"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setQuery("");
                                            if (mobileMode && onCloseMobile) onCloseMobile();
                                            navigate(perfilPath);
                                          }}
                                        >
                                          <ResponsiveImage
                                            src={perf.foto_perfil ? buildImageUrl(perf.foto_perfil) : "/img/logo/LogoLinkServs.png"}
                                            alt={perf.nombre_empresa}
                                            className="w-full h-24 rounded-md object-cover mb-2"
                                            type="profile"
                                          />

                                          <div className="font-medium text-sm text-zinc-800 truncate">{perf.nombre_comercial || perf.nombre_empresa}</div>
                                          <div className="text-xs text-zinc-500 truncate"> {perf.direccion?.provincia}</div>
                                          <div className="text-xs text-zinc-500 truncate">{perf.direccion?.localidad}</div>
                                          <div className="text-xs text-zinc-500 truncate">C.P:{perf.direccion?.codigo_postal}</div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>

  );
};

export default Nav;
